// ==============================================
// Admin › App Library › "Add an app". Starts from the APK set, not a form.
//
// The admin drops base.apk and its split_config files. The browser reads them
// and fills everything the files know: package (read-only), version, the
// app's name, the vendor from the signing certificate, the Android versions
// it needs, sizes and sha256. Only what a file cannot know is left to the
// admin: the one-line description, distribution, region, shelf and plans.
// When the package is already in the library this becomes "add a version",
// never a duplicate. One button creates the app and uploads the set; the
// version starts in Review, as from "Add version". An upload that stopped
// part way resumes when the same files are picked again.
// ==============================================
import { useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowPathIcon, ArrowUpTrayIcon, ExclamationTriangleIcon, InformationCircleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { api } from '../../services/api';
import { androidVersion, findExistingApp, readApkSet, summariseApkSet, uploadName, type PickedApk } from '../../lib/apkSet';
import {
  SHELVES, errText, findStuckDraft, mb, progressText, resumeApkSetVersion, sameFilesAsDraft, shelfOf, uploadApkSetVersion,
  type Availability, type DraftVersion, type UploadProgress,
} from './appLibraryShared';

export interface LibraryApp {
  id: string; packageName: string; name: string; distribution: 'library' | 'skipper_managed';
  versions: Array<DraftVersion & { versionName: string }>;
}
export interface LibraryPlan { id: string; name: string; visibility: string | null }

const input = 'mt-1 w-full rounded-md border border-slate-700 bg-slate-800 px-3 py-2 text-sm text-white';

/** Why this set cannot go onto the existing app, in one plain line, or null. */
function existingProblem(app: LibraryApp, code: number | null): string | null {
  if (app.distribution !== 'library') return `${app.name} is skipper-managed, so Vortex does not serve its files. Switch it to “Vortex serves the APK” in its settings first.`;
  if (code === null) return null;
  const same = app.versions.find((v) => v.versionCode === code);
  // A draft that stopped part way is not a clash: it resumes, or is replaced.
  if (same && findStuckDraft([same], code)) return null;
  if (same) return `${app.name} already has version code ${code} (${same.versionName}). Delete that version first, or pick a newer build.`;
  const newest = app.versions.filter((v) => v.state !== 'archived').sort((a, b) => b.versionCode - a.versionCode)[0];
  if (newest && code < newest.versionCode) return `This build (code ${code}) is older than ${app.name} ${newest.versionName} (code ${newest.versionCode}). Scouts only move forward.`;
  return null;
}

export function AddAppModal({ apps, plans, onClose }: { apps: LibraryApp[]; plans: LibraryPlan[]; onClose: () => void }) {
  const qc = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const [picked, setPicked] = useState<PickedApk[]>([]);
  const [reading, setReading] = useState(false);
  const [name, setName] = useState('');
  const [vendor, setVendor] = useState('');
  const [form, setForm] = useState({ description: '', distribution: 'library' as 'library' | 'skipper_managed', offered: [] as string[], region: '', availability: 'withheld' as Availability });
  const [progress, setProgress] = useState<UploadProgress | null>(null);
  const [error, setError] = useState<string | null>(null);

  const set = summariseApkSet(picked);
  const existing = findExistingApp(apps, set.packageName);
  const blocked = existing ? existingProblem(existing, set.versionCode) : null;
  const stuck = existing && !blocked ? findStuckDraft(existing.versions, set.versionCode) : null;
  const resumable = !!stuck && picked.every((p) => p.sha256) && sameFilesAsDraft(stuck, files());
  const visiblePlans = plans.filter((p) => p.visibility !== 'hidden' || p.name === 'Tester');

  const onFiles = async (list: FileList | File[]) => {
    setError(null); setReading(true);
    const out = await readApkSet(list);
    const s = summariseApkSet(out);
    setPicked(out);
    setName(s.label ?? '');
    setVendor(s.vendor ?? '');
    setReading(false);
  };

  function files() { return picked.map((p) => ({ name: uploadName(p), file: p.file, sha256: p.sha256 ?? '' })); }
  const meta = () => ({ versionName: set.versionName.trim(), versionCode: set.versionCode!, note: null, required: false });

  const save = useMutation({
    mutationFn: async () => {
      if (existing && stuck && resumable) {
        await resumeApkSetVersion(stuck, files(), setProgress);
        return;
      }
      if (existing && stuck) await api.adminAppLibraryDeleteVersion(stuck.id);
      if (existing) {
        await uploadApkSetVersion(existing.id, meta(), files(), setProgress);
        return;
      }
      const app: { id: string } = await api.adminAppLibraryCreateApp({
        package_name: set.packageName, name: name.trim(), vendor: vendor.trim() || null, description: form.description.trim() || null,
        distribution: form.distribution, offered_package_ids: form.offered, region: form.region.trim() || null, availability: form.availability,
      });
      if (form.distribution === 'library') await uploadApkSetVersion(app.id, meta(), files(), setProgress);
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['admin', 'app-library'] }); onClose(); },
    // If the app was created but the upload failed, the refreshed list makes
    // this dialog switch to "add a version", and a stopped upload to "resume".
    onError: (e: unknown) => { setProgress(null); setError(errText(e, 'Could not add the app')); qc.invalidateQueries({ queryKey: ['admin', 'app-library'] }); },
  });

  const uploads = existing ? true : form.distribution === 'library';
  const ready = picked.length > 0 && !reading && !set.problem && !blocked && !!set.packageName
    && (existing ? true : !!name.trim())
    && (!uploads || (!!set.versionName.trim() && (set.versionCode ?? 0) > 0));
  const action = stuck ? (resumable ? 'Resume the upload' : 'Replace the unfinished upload') : existing ? 'Upload and send for review' : uploads ? 'Add app and upload' : 'Add to the library';

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/60 p-4" onClick={() => !save.isPending && onClose()}>
      <div role="dialog" aria-label="Add an app to the library" className="w-full max-w-xl max-h-[92vh] overflow-y-auto rounded-xl border border-slate-700 bg-slate-900 p-5 space-y-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-white">{existing ? `Add a version of ${existing.name}` : 'Add an app to the library'}</h3>
          <button onClick={onClose} disabled={save.isPending} className="text-slate-400 hover:text-white" aria-label="Close"><XMarkIcon className="h-5 w-5" /></button>
        </div>

        {picked.length === 0 && !reading && (
          <div
            className="rounded-lg border-2 border-dashed border-slate-600 px-5 py-8 text-center text-base text-slate-300 hover:border-cyan-500 cursor-pointer"
            onClick={() => inputRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); void onFiles(e.dataTransfer.files); }}
          >
            <ArrowUpTrayIcon className="mx-auto h-7 w-7 mb-2" />
            Drop the APK set here, or click to choose
            <p className="mt-1 text-sm text-slate-400">base.apk plus its split_config files, exactly as pulled from a Play Store install. The name, version and vendor are read from the files.</p>
          </div>
        )}
        <input ref={inputRef} type="file" accept=".apk" multiple aria-label="Choose the APK files" className="hidden" onChange={(e) => { if (e.target.files?.length) void onFiles(e.target.files); e.target.value = ''; }} />
        {reading && <div className="text-sm text-slate-300 flex items-center gap-2"><ArrowPathIcon className="h-4 w-4 animate-spin" /> Reading and hashing the files…</div>}

        {picked.length > 0 && !reading && (
          <>
            <ul className="text-sm divide-y divide-slate-800 rounded-md border border-slate-800">
              {picked.map((p) => (
                <li key={p.file.name} className="flex items-center justify-between gap-3 px-3 py-2">
                  <div className="min-w-0">
                    <div className="font-mono text-slate-200 truncate">{uploadName(p)}</div>
                    <div className="text-sm text-slate-400 font-mono truncate">{p.info ? (p.info.isSplit ? `split ${p.info.splitName}` : 'base') : (p.error ?? 'manifest not read')} · sha256 {p.sha256?.slice(0, 12) ?? '…'}</div>
                  </div>
                  <span className="text-sm text-slate-400 whitespace-nowrap">{mb(p.file.size)}</span>
                </li>
              ))}
            </ul>
            <button type="button" onClick={() => inputRef.current?.click()} disabled={save.isPending} className="text-sm text-cyan-300 hover:text-cyan-200">Choose different files</button>

            {set.problem && <div className="text-sm text-red-300 flex items-start gap-2"><ExclamationTriangleIcon className="h-5 w-5 flex-none" /> {set.problem}</div>}
            {stuck && resumable && <div className="text-sm text-cyan-200 flex items-start gap-2"><InformationCircleIcon className="h-5 w-5 flex-none" /> The upload of {stuck.versionName} stopped part way. These are the same files, so this finishes it.</div>}
            {stuck && !resumable && <div className="text-sm text-amber-300 flex items-start gap-2"><ExclamationTriangleIcon className="h-5 w-5 flex-none" /> The unfinished upload of {stuck.versionName} is not the same files as these. Replace it to start again with these files.</div>}
            {existing && !blocked && !stuck && <div className="text-sm text-cyan-200 flex items-start gap-2"><InformationCircleIcon className="h-5 w-5 flex-none" /> {existing.name} is already in the library, so this adds version {set.versionName} to it.</div>}
            {blocked && <div className="text-sm text-amber-300 flex items-start gap-2"><ExclamationTriangleIcon className="h-5 w-5 flex-none" /> {blocked}</div>}

            {set.packageName && (
              <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 rounded-md bg-slate-800/60 px-3 py-2.5 text-sm">
                <dt className="text-slate-400">Package</dt><dd className="font-mono text-slate-100 break-all">{set.packageName}</dd>
                <dt className="text-slate-400">Version</dt><dd className="font-mono text-slate-100">{set.versionName || '—'} (code {set.versionCode})</dd>
                {set.minSdk !== null && <><dt className="text-slate-400">Needs</dt><dd className="text-slate-100">{androidVersion(set.minSdk)} or newer{set.targetSdk !== null && `, built for ${androidVersion(set.targetSdk)}`}</dd></>}
                <dt className="text-slate-400">Set</dt><dd className="text-slate-100">{picked.length} file{picked.length === 1 ? '' : 's'}, {mb(set.totalBytes)}</dd>
              </dl>
            )}

            {set.packageName && !existing && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm text-slate-300">Name<input className={input} value={name} maxLength={120} onChange={(e) => setName(e.target.value)} /></label>
                    <p className={`mt-1 text-sm ${set.labelGuessed ? 'text-amber-300' : 'text-slate-400'}`}>{set.labelGuessed ? 'Guessed from the package name; the APK’s own name could not be read. Check it.' : 'From the APK.'}</p>
                  </div>
                  <div>
                    <label className="block text-sm text-slate-300">Vendor<input className={input} value={vendor} maxLength={120} onChange={(e) => setVendor(e.target.value)} /></label>
                    <p className="mt-1 text-sm text-slate-400">{set.vendor ? 'From the signing certificate.' : 'No readable signing certificate. Type it if you know it.'}</p>
                  </div>
                </div>
                <label className="block text-sm text-slate-300">What it does, one line<input className={input} placeholder="Charts, routes and AIS for cruising" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></label>
                <div className="grid grid-cols-2 gap-3">
                  <label className="block text-sm text-slate-300">Distribution
                    <select className={input} value={form.distribution} onChange={(e) => setForm({ ...form, distribution: e.target.value as 'library' | 'skipper_managed' })}>
                      <option value="library">Vortex serves the APK</option>
                      <option value="skipper_managed">Skipper-managed (listed, version reported only)</option>
                    </select>
                  </label>
                  <label className="block text-sm text-slate-300">Region (blank = worldwide)<input className={`${input} uppercase`} placeholder="NZ" maxLength={8} value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })} /></label>
                </div>
                <label className="block text-sm text-slate-300">On the Scout
                  <select className={input} value={form.availability} onChange={(e) => setForm({ ...form, availability: e.target.value as Availability })}>
                    {SHELVES.map((sh) => <option key={sh.value} value={sh.value}>{sh.label}</option>)}
                  </select>
                  <span className="mt-1 block text-sm text-slate-400">{shelfOf(form.availability).blurb}</span>
                </label>
                <div>
                  <div className="text-sm text-slate-300 mb-1">Offered to <span className="text-slate-400">(none ticked = every plan)</span></div>
                  <div className="flex flex-wrap gap-2">
                    {visiblePlans.map((p) => {
                      const on = form.offered.includes(p.id);
                      return <button key={p.id} type="button" aria-pressed={on} onClick={() => setForm({ ...form, offered: on ? form.offered.filter((x) => x !== p.id) : [...form.offered, p.id] })} className={`rounded-full px-3 py-1 text-sm font-semibold border ${on ? 'bg-cyan-600 border-cyan-500 text-white' : 'border-slate-600 text-slate-300 hover:border-slate-400'}`}>{p.name.trim()}</button>;
                    })}
                  </div>
                </div>
                {!uploads && <p className="text-sm text-slate-400">Skipper-managed: the app is listed and its installed version reported, but these files are not uploaded.</p>}
              </>
            )}
          </>
        )}

        {progress && <div><div className="text-sm text-slate-300 mb-1">{progressText(progress)}</div><div className="h-2 rounded bg-slate-700 overflow-hidden"><div className="h-full bg-cyan-500" style={{ width: `${progress.pct}%` }} /></div></div>}
        {error && <div className="rounded-md border border-red-800/60 bg-red-900/20 px-3 py-2 text-sm text-red-300">{error}</div>}
        <div className="flex justify-end gap-2 pt-1">
          <button onClick={onClose} disabled={save.isPending} className="rounded-md border border-slate-600 px-3 py-2 text-sm text-slate-300">Cancel</button>
          {picked.length > 0 && <button onClick={() => save.mutate()} disabled={!ready || save.isPending} className="rounded-md bg-cyan-600 px-3 py-2 text-sm font-semibold text-white disabled:opacity-50">{save.isPending ? (progress ? 'Uploading…' : 'Adding…') : action}</button>}
        </div>
      </div>
    </div>
  );
}
